import { FC, ReactNode, useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { NsKey } from 'enums/NsKey'
import { Button } from 'components/ui/Button'
import { GenericLoading, Sizes } from 'components/ui/Loader'
import { DownloadIcon, PrintIcon, XMarkIcon, MailIcon } from 'components/icons/Icons'
import { Maximize2, Minimize2, ZoomIn, ZoomOut, RotateCw } from 'lucide-react'
import {
  EmailComposer,
  LeadEmailComposer,
  EmailConfig,
  isLeadEmailConfig,
  isStandardEmailConfig
} from './EmailComposer'
import { useDocumentBlob } from './useDocumentBlob'

export type DocumentType = 'pdf' | 'image'
export type ViewMode = 'document' | 'mail'

export interface DocumentViewerAction {
  label: string
  icon?: ReactNode
  onClick: () => void
  disabled?: boolean
  color?: 'primary' | 'secondary' | 'slate' | 'red'
  outline?: boolean
}

export type { EmailConfig } from './EmailComposer'

export interface DocumentViewerProps {
  title: string
  storedFileId?: string
  blobUrl?: string | null
  documentType?: DocumentType
  mimeType?: string
  onClose: () => void
  actions?: DocumentViewerAction[]
  showDefaultActions?: boolean
  emailConfig?: EmailConfig
  /** Rendered inside another layout: no close button, no global Escape listener, height given by the parent. */
  embedded?: boolean
}

const IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'image/bmp'
]

const PDF_MIME_TYPES = ['application/pdf']

export const getDocumentTypeFromMimeType = (mimeType?: string): DocumentType => {
  if (!mimeType) return 'pdf'
  if (IMAGE_MIME_TYPES.includes(mimeType)) return 'image'
  if (PDF_MIME_TYPES.includes(mimeType)) return 'pdf'
  return 'pdf'
}

export const getExtensionFromMimeType = (mimeType?: string): string => {
  if (!mimeType) return 'pdf'
  const mimeToExt: Record<string, string> = {
    'application/pdf': 'pdf',
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/svg+xml': 'svg',
    'image/bmp': 'bmp'
  }
  return mimeToExt[mimeType] || 'pdf'
}

export const DocumentViewer: FC<DocumentViewerProps> = ({
  title,
  storedFileId,
  blobUrl: externalBlobUrl,
  documentType,
  mimeType,
  onClose,
  actions = [],
  showDefaultActions = true,
  emailConfig,
  embedded = false
}) => {
  const { t } = useTranslation(NsKey.Common)
  const [scale, setScale] = useState(100)
  const [rotation, setRotation] = useState(0)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [viewMode, setViewMode] = useState<ViewMode>('document')

  const { blobUrl, isLoading } = useDocumentBlob(storedFileId, externalBlobUrl, mimeType)

  const resolvedDocumentType = documentType || getDocumentTypeFromMimeType(mimeType)
  const fileExtension = getExtensionFromMimeType(mimeType)

  const handleZoomIn = useCallback(() => {
    setScale(prev => Math.min(prev + 25, 200))
  }, [])

  const handleZoomOut = useCallback(() => {
    setScale(prev => Math.max(prev - 25, 50))
  }, [])

  const handleRotate = useCallback(() => {
    setRotation(prev => (prev + 90) % 360)
  }, [])

  const handleDownload = useCallback(() => {
    if (blobUrl) {
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = `${title}.${fileExtension}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }, [blobUrl, title, fileExtension])

  const handlePrint = useCallback(() => {
    if (blobUrl) {
      const printWindow = window.open(blobUrl, '_blank')
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print()
        }
      }
    }
  }, [blobUrl])

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev)
  }, [])

  const handleOpenEmailView = useCallback(() => {
    setViewMode('mail')
  }, [])

  const handleBackToDocument = useCallback(() => {
    setViewMode('document')
  }, [])

  useEffect(() => {
    if (embedded) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isFullscreen) {
          setIsFullscreen(false)
        } else {
          onClose()
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [embedded, isFullscreen, onClose])

  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isFullscreen])

  const handleOpenInNewTab = useCallback(() => {
    if (blobUrl) {
      window.open(blobUrl, '_blank', 'noopener')
    }
  }, [blobUrl])

  const defaultActions: DocumentViewerAction[] = showDefaultActions ? [
    ...(embedded ? [{
      label: t('COMMON.ACTIONS.OPEN_IN_NEW_TAB'),
      onClick: handleOpenInNewTab,
      disabled: !blobUrl,
      outline: true
    }] : []),
    {
      label: t('COMMON.ACTIONS.DOWNLOAD'),
      icon: <DownloadIcon className="w-4 h-4" />,
      onClick: handleDownload,
      disabled: !blobUrl,
      outline: true
    },
    {
      label: t('COMMON.ACTIONS.PRINT'),
      icon: <PrintIcon className="w-4 h-4" />,
      onClick: handlePrint,
      disabled: !blobUrl,
      outline: true
    }
  ] : []

  const emailAction: DocumentViewerAction | null = emailConfig ? {
    label: t('COMMON.ACTIONS.SEND_EMAIL'),
    icon: <MailIcon className="w-4 h-4" />,
    onClick: handleOpenEmailView,
    disabled: !blobUrl,
    color: 'primary'
  } : null

  const allActions = [...defaultActions, ...actions, ...(emailAction ? [emailAction] : [])]

  const renderHeader = (isInFullscreen: boolean) => (
    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50 shrink-0">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-semibold text-gray-900 truncate max-w-md">
          {title}
        </h2>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1 px-2 py-1 bg-white rounded-lg border border-gray-200">
          <button
            onClick={handleZoomOut}
            disabled={scale <= 50}
            className="p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            title={t('COMMON.ACTIONS.ZOOM_OUT')}
          >
            <ZoomOut className="w-4 h-4 text-gray-600" />
          </button>
          <span className="text-sm text-gray-600 min-w-[3rem] text-center">{scale}%</span>
          <button
            onClick={handleZoomIn}
            disabled={scale >= 200}
            className="p-1 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            title={t('COMMON.ACTIONS.ZOOM_IN')}
          >
            <ZoomIn className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        <button
          onClick={handleRotate}
          className="p-2 hover:bg-gray-100 rounded-lg border border-gray-200 bg-white"
          title={t('COMMON.ACTIONS.ROTATE')}
        >
          <RotateCw className="w-4 h-4 text-gray-600" />
        </button>

        {!embedded && (
          <button
            onClick={toggleFullscreen}
            className="p-2 hover:bg-gray-100 rounded-lg border border-gray-200 bg-white"
            title={isInFullscreen ? t('COMMON.ACTIONS.EXIT_FULLSCREEN') : t('COMMON.ACTIONS.FULLSCREEN')}
          >
            {isInFullscreen ? (
              <Minimize2 className="w-4 h-4 text-gray-600" />
            ) : (
              <Maximize2 className="w-4 h-4 text-gray-600" />
            )}
          </button>
        )}

        {!embedded && (
          <button
            onClick={isInFullscreen ? toggleFullscreen : onClose}
            className="p-2 hover:bg-gray-100 rounded-lg border border-gray-200 bg-white ml-2"
            title={t('COMMON.ACTIONS.CLOSE')}
          >
            <XMarkIcon className="w-5 h-5 text-gray-600" />
          </button>
        )}
      </div>
    </div>
  )

  const renderPdfContent = () => (
    <iframe
      key={`pdf-${scale}`}
      src={`${blobUrl}#toolbar=0&navpanes=0&zoom=${scale}`}
      className="bg-white shadow-lg w-full h-full"
      style={{ border: 'none' }}
      title={title}
    />
  )

  const renderImageContent = () => (
    <img
      src={blobUrl!}
      alt={title}
      className="max-w-full max-h-full object-contain shadow-lg bg-white"
      style={{
        transform: `scale(${scale / 100}) rotate(${rotation}deg)`,
        transition: 'transform 0.3s ease'
      }}
    />
  )

  const renderContent = () => (
    <div className="flex-1 relative bg-gray-100 overflow-auto">
      {isLoading ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <GenericLoading size={Sizes.lg} loading />
            <p className="text-gray-600">{t('COMMON.LABELS.LOADING')}</p>
          </div>
        </div>
      ) : blobUrl ? (
        <div
          className="w-full h-full flex items-center justify-center p-4"
          style={resolvedDocumentType === 'pdf' ? {
            transform: `rotate(${rotation}deg)`,
            transition: 'transform 0.3s ease'
          } : undefined}
        >
          {resolvedDocumentType === 'pdf' ? renderPdfContent() : renderImageContent()}
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <p className="text-gray-500">{t('COMMON.ERRORS.DOCUMENT_NOT_FOUND')}</p>
        </div>
      )}
    </div>
  )

  const renderFooter = () => allActions.length > 0 && (
    <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 shrink-0">
      {allActions.map((action, index) => {
        const buttonProps = action.outline
          ? { outline: true as const }
          : { color: action.color || 'primary' as const }

        return (
          <Button
            key={index}
            {...buttonProps}
            onClick={action.onClick}
            disabled={action.disabled}
          >
            {action.icon && <span className="mr-2">{action.icon}</span>}
            {action.label}
          </Button>
        )
      })}
    </div>
  )

  // Email view mode
  if (viewMode === 'mail' && emailConfig) {
    // Use LeadEmailComposer for lead variant
    if (isLeadEmailConfig(emailConfig)) {
      return (
        <LeadEmailComposer
          emailConfig={emailConfig}
          documentTitle={title}
          documentType={resolvedDocumentType}
          fileExtension={fileExtension}
          isFullscreen={isFullscreen}
          onClose={onClose}
          onBack={handleBackToDocument}
          onToggleFullscreen={toggleFullscreen}
        />
      )
    }

    // Use standard EmailComposer for standard variant
    if (isStandardEmailConfig(emailConfig)) {
      return (
        <EmailComposer
          emailConfig={emailConfig}
          documentTitle={title}
          documentType={resolvedDocumentType}
          fileExtension={fileExtension}
          isFullscreen={isFullscreen}
          onClose={onClose}
          onBack={handleBackToDocument}
          onToggleFullscreen={toggleFullscreen}
        />
      )
    }
  }

  // Document view mode (default)
  if (isFullscreen) {
    return (
      <>
        <div className="hidden" />
        {createPortal(
          <div className="fixed inset-0 z-[9999] flex flex-col bg-white">
            {renderHeader(true)}
            {renderContent()}
            {renderFooter()}
          </div>,
          document.body
        )}
      </>
    )
  }

  return (
    <div className={`flex flex-col bg-white rounded-2xl overflow-hidden ${embedded ? 'h-full' : 'h-[80vh]'}`}>
      {renderHeader(false)}
      {renderContent()}
      {renderFooter()}
    </div>
  )
}
